import { ApolloClient } from '@apollo/client';
import { BUSINESS } from '~/funnels/business/config';
import { COMMUNITY_SOLAR_FUNNEL_ID } from '~/funnels/community-solar/config';
import { GO_SOLAR_FUNNEL_ID } from '~/funnels/go-solar/config';
import { I_SOLAR_UCB } from '~/funnels/isolar-ucb/config';
import { fetchFunnelPacket, fetchNyLmiZoneOptions, fetchIsInLmiZone } from '~/services';

import { FetchFunnelPacketResponse } from '~/services/fetch-funnel-packet';
import { FetchIsInLmiZoneParams, FetchIsInLmiZoneResponse } from '~/services/fetch-is-in-lmi-zone';
import {
  FetchNyLmiZoneOptionsParams,
  FetchNyLmiZoneOptionsResponse,
} from '~/services/fetch-ny-dac-ez-zone';
import { getUserDocusignEnvelopes } from '~/services/user-docusign-envelopes';
import { EnrollmentDataSource } from '~/typings/context';
import {
  Flex_ExchangeTokenQuery,
  Flex_ExchangeTokenQueryVariables,
  FunnelTrack,
} from '~/typings/graphql';
import { errors } from '~/utils/error-messages';
import { decodeJwt } from '~/utils/for-warbler';
import { DocusignSignedStatus, store } from '~/utils/store';
import { trackError } from '~/utils/tracking';
import { UnparsedQueryParams } from '../../types';
import { EXCHANGE_TOKEN } from './handle-resuming-user.queries';

export const updateLocalStorageFromPacket = (packet: FetchFunnelPacketResponse) => {
  store.set('UTILITY_SELECTION', packet.utility);
  store.set('ACCOUNT_ID', packet.account.id);
  store.set('PROOF_DOC_SKIPPED', !!packet.user.originationSource?.queryParams?.proofDocSkipped);
  store.set(
    'DOCUSIGN_USER_SIGNED_STATUS',
    packet.user.originationSource?.queryParams?.docusignSignedStatus as DocusignSignedStatus,
  );
  store.set(
    'ENROLLMENT_DATA_SOURCE_NAME',
    packet.user.originationSource?.queryParams?.enrollmentDataSourceName as EnrollmentDataSource,
  );
};

export const handleResumingUser = async ({
  apolloClient,
  params: {
    accountId,
    customPlanId,
    selectedUtilityAccountId,
    loginToken,
    userId,
    utilityAccountId,
    enrollmentOption,
  },
}: {
  apolloClient: ApolloClient<{}>;
  params: UnparsedQueryParams;
}) => {
  const isComingFromWelcomeBackFunnel = selectedUtilityAccountId && userId && store.get('TOKEN');
  const userDocusignEnvelopes = await getUserDocusignEnvelopes(userId!);
  if (isComingFromWelcomeBackFunnel) {
    const packet = await fetchFunnelPacket({
      userId,
      utilityAccountId: selectedUtilityAccountId,
    });
    const { nyLmiZoneOptions, isInLmiZoneOptions } = await fetchLmiZoneOptions(packet);
    const forceWaitlist = isForceWaitlist(packet);
    updateLocalStorageFromPacket(packet);
    return {
      data: {
        packet,
        nyLmiZoneOptions,
        isInLmiArea: isInLmiZoneOptions?.lmiZoneOptions.isInLmiArea,
        id: userId,
        enrollmentOption,
        funnelVnmProjectId: packet.funnelVnmProjectId,
        forceWaitlist,
        userDocusignEnvelopes: userDocusignEnvelopes?.user?.userDocuSignEnvelopes || [],
      },
    };
  }

  if (!loginToken) return;

  // remove any old cached references
  store.clearAll();

  try {
    const result = await apolloClient.query<
      Flex_ExchangeTokenQuery,
      Flex_ExchangeTokenQueryVariables
    >({
      query: EXCHANGE_TOKEN,
      variables: { loginToken },
    });

    const encodedJwt = result.data.tokenExchange.token;
    const decodedJwt = decodeJwt(encodedJwt);
    const { userId } = decodedJwt;
    if (!decodedJwt.valid || !userId) {
      throw new Error('Authorization expired');
    }

    store.set('TOKEN', encodedJwt);
    store.set('USER_ID', userId);

    const packet = await fetchFunnelPacket({
      accountId,
      customPlanId,
      userId,
      utilityAccountId,
    });

    updateLocalStorageFromPacket(packet);

    return {
      data: {
        packet,
        id: userId,
        enrollmentOption,
        funnelVnmProjectId: packet.funnelVnmProjectId,
        userDocusignEnvelopes: userDocusignEnvelopes?.user?.userDocuSignEnvelopes || [],
      },
    };
  } catch (e) {
    const error = e.message;
    if (error.includes('Authorization expired')) {
      return { error: errors.expiredLoginToken };
    } else {
      trackError(new Error(`Resuming user error: ${error}`));
      return { error };
    }
  }
};

/*
 * Force waitlist logic
 * 1. if the user is in NM and is coming from the waitlist, force them to the waitlist
 * this is to handle resume user flow of force waitlisted users
 */
const isForceWaitlist = (packet: FetchFunnelPacketResponse): boolean =>
  packet.utility?.state.abbreviation === 'NM' &&
  packet.user?.originationSource?.funnel === FunnelTrack.Waitlist;

const fetchLmiZoneOptions = async (packet: FetchFunnelPacketResponse) => {
  let nyLmiZoneOptions: FetchNyLmiZoneOptionsResponse | undefined;
  if (packet.utility?.state.abbreviation === 'NY') {
    const params: FetchNyLmiZoneOptionsParams = {
      utilityAccountId: packet.utilityAccount.id,
    };
    nyLmiZoneOptions = await fetchNyLmiZoneOptions(params);
  }

  let isInLmiZoneOptions: FetchIsInLmiZoneResponse | undefined;
  if (packet.utility?.state.abbreviation === 'VA') {
    const shouldUseVALmiFlow = [
      COMMUNITY_SOLAR_FUNNEL_ID,
      BUSINESS,
      I_SOLAR_UCB,
      GO_SOLAR_FUNNEL_ID,
    ].includes(packet.user?.originationSource?.funnel as FunnelTrack);
    if (shouldUseVALmiFlow) {
      const params: FetchIsInLmiZoneParams = {
        utilityAccountId: packet.utilityAccount.id,
      };
      isInLmiZoneOptions = await fetchIsInLmiZone(params);
    } else {
      isInLmiZoneOptions = {
        lmiZoneOptions: {
          isInLmiArea: false,
          error: false,
          isInCensusTract: false,
        },
      };
    }
  }

  return { nyLmiZoneOptions, isInLmiZoneOptions };
};
